// Heimatgut instant search — overlay + predictive fetch.
// Server renders results via section_id=heimat-predictive-search.
// No redirect to /search on keystroke — redirect only on explicit submit.

(function () {
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 180;
  const SECTION_ID = 'heimat-predictive-search';

  function init() {
    const overlay = document.querySelector('[data-heimat-search]');
    if (!overlay) return;

    const input = overlay.querySelector('[data-heimat-search-input]');
    const clearBtn = overlay.querySelector('[data-heimat-search-clear]');
    const resultsEl = overlay.querySelector('[data-heimat-search-results]');
    const loadingEl = overlay.querySelector('[data-heimat-search-loading]');
    const form = overlay.querySelector('[data-heimat-search-form]');
    const openers = document.querySelectorAll('[data-heimat-search-open]');
    const closers = overlay.querySelectorAll('[data-heimat-search-close]');
    const chips = overlay.querySelectorAll('[data-heimat-search-query]');

    if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

    let lastTerm = '';
    let debounceTimer = null;
    let activeController = null;

    function setState(state) {
      overlay.setAttribute('data-state', state);
      loadingEl.hidden = state !== 'loading';
    }

    function open() {
      overlay.setAttribute('data-open', 'true');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('heimat-search-open');
      setTimeout(() => input && input.focus(), 60);
    }

    function close() {
      overlay.setAttribute('data-open', 'false');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('heimat-search-open');
      if (activeController) { activeController.abort(); activeController = null; }
    }

    function reset() {
      input.value = '';
      lastTerm = '';
      resultsEl.innerHTML = '';
      clearBtn.hidden = true;
      overlay.setAttribute('data-has-query', 'false');
      setState('idle');
    }

    function updateClearBtn() {
      clearBtn.hidden = !input.value;
      overlay.setAttribute('data-has-query', input.value ? 'true' : 'false');
    }

    async function runSearch(term) {
      if (activeController) activeController.abort();
      activeController = new AbortController();

      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=${SECTION_ID}&resources[type]=product`;

      setState('loading');
      try {
        const res = await fetch(url, { signal: activeController.signal });
        if (!res.ok) throw new Error('search failed');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fragment = doc.querySelector('.shopify-section') || doc.body;
        resultsEl.innerHTML = fragment.innerHTML;
        setState('results');
      } catch (err) {
        if (err.name === 'AbortError') return;
        resultsEl.innerHTML = '';
        setState('idle');
      }
    }

    function onInput() {
      updateClearBtn();
      const term = input.value.trim();
      if (term === lastTerm) return;
      lastTerm = term;

      clearTimeout(debounceTimer);

      if (term.length < MIN_CHARS) {
        resultsEl.innerHTML = '';
        setState('idle');
        return;
      }

      debounceTimer = setTimeout(() => runSearch(term), DEBOUNCE_MS);
    }

    // Capture phase + stopImmediatePropagation blocks third-party apps
    // (e.g. cloud-search-mobile) from intercepting the trigger click.
    openers.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        open();
      }, true);
    });

    closers.forEach(btn => btn.addEventListener('click', (e) => {
      e.preventDefault();
      close();
    }));

    clearBtn.addEventListener('click', () => {
      reset();
      input.focus();
    });

    input.addEventListener('input', onInput);

    chips.forEach(chip => chip.addEventListener('click', () => {
      const q = chip.getAttribute('data-heimat-search-query') || '';
      input.value = q;
      input.focus();
      lastTerm = '';
      onInput();
    }));

    form.addEventListener('submit', (e) => {
      if (!input.value.trim()) e.preventDefault();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.getAttribute('data-open') === 'true') {
        close();
      }
      // Keyboard shortcut: Cmd/Ctrl+K opens search
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        overlay.getAttribute('data-open') === 'true' ? close() : open();
      }
    });

    setState('idle');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// Inline desktop search — pill input + anchored dropdown, no overlay.
(function () {
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 180;
  const SECTION_ID = 'heimat-predictive-search';

  function initInline() {
    const wrap = document.querySelector('[data-heimat-inline-search]');
    if (!wrap) return;

    const input    = wrap.querySelector('[data-heimat-inline-input]');
    const clearBtn = wrap.querySelector('[data-heimat-inline-clear]');
    const idleEl   = wrap.querySelector('[data-heimat-inline-idle]');
    const loadingEl = wrap.querySelector('[data-heimat-inline-loading]');
    const resultsEl = wrap.querySelector('[data-heimat-inline-results]');

    if (!input) return;

    let lastTerm = '';
    let debounceTimer = null;
    let activeController = null;

    function open() { wrap.setAttribute('data-open', 'true'); }
    function close() {
      wrap.setAttribute('data-open', 'false');
      if (activeController) { activeController.abort(); activeController = null; }
    }

    function showIdle() {
      if (idleEl) idleEl.hidden = false;
      if (resultsEl) resultsEl.innerHTML = '';
      if (loadingEl) loadingEl.hidden = true;
    }

    function updateClear() {
      if (clearBtn) clearBtn.hidden = !input.value;
    }

    async function runSearch(term) {
      if (activeController) activeController.abort();
      activeController = new AbortController();

      if (idleEl) idleEl.hidden = true;
      if (loadingEl) loadingEl.hidden = false;

      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}&section_id=${SECTION_ID}&resources[type]=product`;
      try {
        const res = await fetch(url, { signal: activeController.signal });
        if (!res.ok) throw new Error('search failed');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const frag = doc.querySelector('.shopify-section') || doc.body;
        if (resultsEl) resultsEl.innerHTML = frag.innerHTML;
        if (loadingEl) loadingEl.hidden = true;
      } catch (err) {
        if (err.name === 'AbortError') return;
        if (resultsEl) resultsEl.innerHTML = '';
        showIdle();
      }
    }

    input.addEventListener('focus', () => {
      open();
      if (!input.value) showIdle();
    });

    input.addEventListener('input', () => {
      updateClear();
      const term = input.value.trim();
      if (term === lastTerm) return;
      lastTerm = term;
      clearTimeout(debounceTimer);
      if (term.length < MIN_CHARS) { showIdle(); return; }
      debounceTimer = setTimeout(() => runSearch(term), DEBOUNCE_MS);
    });

    if (clearBtn) {
      // Use pointerdown so we win the race against the input's blur handlers
      // and don't get swallowed by other click listeners on the page.
      clearBtn.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        input.value = '';
        lastTerm = '';
        updateClear();
        showIdle();
        close();
        input.blur();
      });
    }

    // Close when clicking outside the wrap, or on the wrap's own padding area.
    document.addEventListener('click', (e) => {
      if (!wrap.contains(e.target) || e.target === wrap) close();
    }, true);

    // Close when focus moves outside (e.g. Tab key).
    document.addEventListener('focusin', (e) => {
      if (!wrap.contains(e.target)) close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && wrap.getAttribute('data-open') === 'true') {
        close();
        input.blur();
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (wrap.getAttribute('data-open') === 'true') {
          close(); input.blur();
        } else {
          input.focus();
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInline);
  } else {
    initInline();
  }
})();
